import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, from, map, Observable, of, shareReplay } from "rxjs";
import { ApiService } from "src/app/services/api.service";
import { DialogService } from "src/app/services/dialog.service";
import { EmailService } from "src/app/services/email-service";
import { WorkflowDefinition } from "../components/workflow-designer/type-driven-dynamic-form";

export { WorkflowDefinition } from "../components/workflow-designer/type-driven-dynamic-form";

export interface FieldOption {
  label: string;
  value: any;
}

@Injectable({
  providedIn: "root",
})
export class WorkflowService {
  private statuses$?: Observable<FieldOption[]>;
  private priorities$?: Observable<FieldOption[]>;
  private taskTypes$?: Observable<FieldOption[]>;
  private eventTypes$?: Observable<FieldOption[]>;
  // Per-brand cache of email-template option lists. shareReplay(1) keeps the
  // result hot for the rest of the session so opening multiple Email Alert
  // steps for the same brand doesn't refire the network call.
  private emailTemplatesByBrand: Map<number, Observable<FieldOption[]>> = new Map();

  private useMockData = false;

  // Raw arrays preserved alongside the cached `FieldOption[]` observables, so
  // consumers that need the original API objects (e.g. for kanban-column
  // mapping by status_name) can pull them synchronously.
  private rawStatuses: any[] = [];
  private rawPriorities: any[] = [];
  private rawTypes: any[] = [];

  getRawStatuses(): any[] { return this.rawStatuses; }
  getRawPriorities(): any[] { return this.rawPriorities; }
  getRawTypes(): any[] { return this.rawTypes; }

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private dialogService: DialogService,
    private emailService: EmailService
  ) {}

  /**
   * Centralised failure handler for API calls. Logs the error, surfaces a
   * user-visible snackbar with the server message when available, and swaps
   * the stream onto a safe fallback so subscribers can keep their existing
   * null/empty-array guards.
   */
  private handleError<T>(op: string, err: any, fallback: T): Observable<T> {
    const serverMsg =
      err?.error?.message ||
      err?.error?.error ||
      err?.message ||
      "Request failed";
    console.error(`[WorkflowService] ${op} failed:`, err);
    this.dialogService.SnackBar(`${op}: ${serverMsg}`, "Close");
    return of(fallback);
  }

  getTaskStatuses(): Observable<FieldOption[]> {
    if (!this.statuses$) {
      if (this.useMockData) {
        const staticStatuses: FieldOption[] = [
          { label: "Todo", value: "todo" },
          { label: "In Progress", value: "inprogress" },
          { label: "Blocked", value: "blocked" },
          { label: "Done", value: "done" },
        ];
        this.statuses$ = of(this.sortStatuses(staticStatuses)).pipe(shareReplay(1));
      } else {
        this.statuses$ = this.apiService.post('api/task-statuses/get-all', {}).pipe(
          map((res: any) => {
            this.rawStatuses = this.extractItems(res);
            return this.sortStatuses(this.toFieldOptions(res, 'status_name', 'task_status_id'));
          }),
          catchError((err) => {
            console.error('Failed to load task statuses:', err);
            return of([]);
          }),
          shareReplay(1)
        );
      }
    }

    return this.statuses$;
  }

  getTaskPriorities(): Observable<FieldOption[]> {
    if (!this.priorities$) {
      if (this.useMockData) {
        const staticPriorities: FieldOption[] = [
          { label: "Low", value: 1 },
          { label: "Medium", value: 2 },
          { label: "High", value: 3 },
        ];
        this.priorities$ = of(this.sortPriorities(staticPriorities)).pipe(shareReplay(1));
      } else {
        this.priorities$ = this.apiService.post('api/task-priorities/get-all', {}).pipe(
          map((res: any) => {
            this.rawPriorities = this.extractItems(res);
            return this.sortPriorities(this.toFieldOptions(res, 'priority_name', 'task_priority_id'));
          }),
          catchError((err) => {
            console.error('Failed to load task priorities:', err);
            return of([]);
          }),
          shareReplay(1)
        );
      }
    }

    return this.priorities$;
  }

  private sortStatuses(options: FieldOption[]): FieldOption[] {
    const order = ['open', 'inprogress', 'in progress', 'in-progress', 'cancelled', 'canceled', 'done', 'complete'];
    return [...options].sort((a, b) => {
      const normalize = (s: string) => s.toLowerCase().replace(/[\s_-]/g, '');
      const ai = order.findIndex(o => normalize(a.label).includes(normalize(o)));
      const bi = order.findIndex(o => normalize(b.label).includes(normalize(o)));
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }

  private sortPriorities(options: FieldOption[]): FieldOption[] {
    const order = ['high', 'medium', 'normal', 'low'];
    return [...options].sort((a, b) => {
      const ai = order.findIndex(o => a.label.toLowerCase().includes(o));
      const bi = order.findIndex(o => b.label.toLowerCase().includes(o));
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }

  getTaskTypes(): Observable<FieldOption[]> {
    if (!this.taskTypes$) {
      if (this.useMockData) {
        const staticTypes: FieldOption[] = [
          { label: "Patient FollowUp", value: 1 },
          { label: "Prescriber FollowUp", value: 2 },
          { label: "Account FollowUp", value: 3 },
        ];
        this.taskTypes$ = of(staticTypes).pipe(shareReplay(1));
      } else {
        this.taskTypes$ = this.apiService.post('api/task-types/get-all', {}).pipe(
          map((res: any) => {
            this.rawTypes = this.extractItems(res);
            return this.toFieldOptions(res, 'type_name', 'task_type_id');
          }),
          catchError((err) => {
            console.error('Failed to load task types:', err);
            return of([]);
          }),
          shareReplay(1)
        );
      }
    }

    return this.taskTypes$;
  }

  /**
   * Per-brand list of saved email templates, mapped to FieldOption shape so it
   * slots into the workflow-designer's existing opts_api/optionCache flow.
   * Result is cached for the session so repeated Email Alert step opens hit
   * memory, not the network.
   */
  getEmailTemplatesForBrand(brandKey: number): Observable<FieldOption[]> {
    const cached = this.emailTemplatesByBrand.get(brandKey);
    if (cached) return cached;

    const stream$ = from(this.emailService.getExtractEmailTemplates(brandKey)).pipe(
      map((res: any) => {
        const items = Array.isArray(res) ? res : this.extractItems(res);
        return items.map((item: any) => ({
          label: item?.template_name ?? '',
          value: item?.email_template_key ?? null,
        }));
      }),
      catchError((err) => this.handleError<FieldOption[]>('Load email templates', err, [])),
      shareReplay(1)
    );
    this.emailTemplatesByBrand.set(brandKey, stream$);
    return stream$;
  }

  getDefinitions(filters?: any): Observable<WorkflowDefinition[]> {
    return this.apiService.post('api/workflows/get-all', filters || {}).pipe(
      map((res: any) => this.extractItems(res) as WorkflowDefinition[]),
      catchError((err) => this.handleError('Load workflows', err, [] as WorkflowDefinition[]))
    );
  }

  createDefinition(payload: { wf_name: string; description?: string; created_by?: string }): Observable<WorkflowDefinition | null> {
    return this.apiService.post('api/workflows/create', payload).pipe(
      map((res: any) => {
        if (res?.data?.wf_def_id) return res.data as WorkflowDefinition;
        return res as WorkflowDefinition;
      }),
      catchError((err) => this.handleError<WorkflowDefinition | null>('Create workflow', err, null))
    );
  }

  loadFull(wfDefId: number): Observable<any> {
    return this.apiService.post('api/workflows/load-full', { wf_def_id: wfDefId }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError('Load workflow', err, null))
    );
  }

  saveFull(wfDefId: number, payload: any): Observable<any> {
    return this.apiService.post('api/workflows/save-full', { wf_def_id: wfDefId, ...payload }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError('Save workflow', err, null))
    );
  }

  activateDefinition(wfDefId: number, activatedBy: string): Observable<any> {
    return this.apiService.post('api/workflows/activate', { wf_def_id: wfDefId, activated_by: activatedBy }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError('Activate workflow', err, null))
    );
  }

  pauseDefinition(wfDefId: number): Observable<any> {
    return this.apiService.post('api/workflows/pause', { wf_def_id: wfDefId }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError('Pause workflow', err, null))
    );
  }

  updateDefinition(payload: any): Observable<any> {
    return this.apiService.post('api/workflows/update', payload).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError('Update workflow', err, null))
    );
  }

  deleteDefinition(wfDefId: number): Observable<any> {
    return this.apiService.post('api/workflows/delete', { wf_def_id: wfDefId }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError('Delete workflow', err, null))
    );
  }

  getRuns(filters?: any): Observable<any[]> {
    return this.apiService.post('api/workflow-runs/get-all', filters || {}).pipe(
      map((res: any) => this.extractItems(res)),
      catchError((err) => this.handleError('Load workflow runs', err, [] as any[]))
    );
  }

  cancelRun(wfRunId: number): Observable<any> {
    return this.apiService.post('api/workflow-runs/cancel', { wf_run_id: wfRunId }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError('Cancel run', err, null))
    );
  }

  getEventTypes(): Observable<FieldOption[]> {
    if (!this.eventTypes$) {
      this.eventTypes$ = this.apiService.post('api/workflows/event-types/get-all', {}).pipe(
        map((res: any) => this.toFieldOptions(res, 'event_type_name', 'event_type_key')),
        catchError((err) => this.handleError<FieldOption[]>('Load event types', err, [])),
        shareReplay(1)
      );
    }
    return this.eventTypes$;
  }

  getStepTypes(): Observable<any[]> {
    return this.apiService.post('api/workflows/step-types/get-all', {}).pipe(
      map((res: any) => this.extractItems(res)),
      catchError((err) => {
        console.error('Failed to load step types:', err);
        return of([]);
      })
    );
  }

  /** Extract a flat array of items from an API response that might be a bare
   *  array, `{data:[]}`, `{items:[]}`, `{result:[]}`. */
  private extractItems(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.items)) return res.items;
    if (Array.isArray(res?.result)) return res.result;
    return [];
  }

  /** Map an API response into FieldOption[] using configurable label/value
   *  field names. Internally delegates to `extractItems`. */
  private toFieldOptions(res: any, labelField: string, valueField: string): FieldOption[] {
    return this.extractItems(res).map((item) => ({
      label: item?.[labelField] ?? '',
      value: item?.[valueField] ?? null,
    }));
  }

  GetpatientStatusCodeFilter (client_key: number, brand_key: string): Observable<any> {
    return this.apiService.post('frm-glks/patient-reason-code-filter', { client_key: client_key, brand_key: brand_key }).pipe(
      map((res: any) => res),
      catchError((err) => this.handleError('Get patient status', err, null))
    );
  }

}
